import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import EmptyState from '../../components/common/EmptyState';
import RichTextEditor from '../../components/common/RichTextEditor';
import BackButton from '../../components/common/BackButton';
import { stripHTML } from '../../utils/htmlSanitizer';
import { formatRelativeTime } from '../../utils/dateUtils';
import './PendingRequests.css';

const ITEMS_PER_PAGE = 5;

function PendingRequests() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [editingMessage, setEditingMessage] = useState({});
  const [processingApproval, setProcessingApproval] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch pending approvals
  useEffect(() => {
    const fetchPendingApprovals = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;
        const sentData = await apiService.getTransactions(idToken, { type: 'sent', limit: 100 });
        const sent = sentData.transactions || [];
        const pendingRequests = sent.filter(tx => tx.pending === true || tx.pending === 1);
        setPendingApprovals(pendingRequests);
      } catch (err) {
        console.error('Error fetching pending approvals:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingApprovals();
  }, [auth.isAuthenticated, auth.user]);

  // Calculate pagination
  const totalPages = Math.ceil(pendingApprovals.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentItems = pendingApprovals.slice(startIndex, endIndex);

  // Handle message edit for pending approval
  const handleMessageEdit = (txId, newMessage) => {
    setEditingMessage(prev => ({
      ...prev,
      [txId]: newMessage
    }));
  };

  // Approve pending request
  const handleApprove = async (tx) => {
    const updatedMessage = editingMessage[tx.tx_id] !== undefined ? editingMessage[tx.tx_id] : tx.description;

    // Validate that description is not empty
    const plainText = stripHTML(updatedMessage).trim();
    if (!plainText) {
      alert('Message cannot be empty. Please provide a description for the coin.');
      return;
    }

    setProcessingApproval(prev => ({ ...prev, [tx.tx_id]: 'approving' }));

    try {
      const idToken = auth.user.id_token;

      // Approve the transaction and update coin description if edited
      const description = (editingMessage[tx.tx_id] !== undefined && editingMessage[tx.tx_id] !== tx.description)
        ? editingMessage[tx.tx_id].trim()
        : undefined;
      await apiService.approveTransaction(idToken, tx.tx_id, description);

      // Refresh pending approvals
      const sentData = await apiService.getTransactions(idToken, { type: 'sent', limit: 100 });
      const sent = sentData.transactions || [];
      const pendingRequests = sent.filter(tx => tx.pending === true || tx.pending === 1);
      setPendingApprovals(pendingRequests);

      // Clear editing state
      setEditingMessage(prev => {
        const newState = { ...prev };
        delete newState[tx.tx_id];
        return newState;
      });

      // Adjust current page if needed
      const newTotalPages = Math.ceil(pendingRequests.length / ITEMS_PER_PAGE);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    } catch (err) {
      console.error('Error approving request:', err);
      alert(`Failed to approve: ${err.message}`);
    } finally {
      setProcessingApproval(prev => {
        const newState = { ...prev };
        delete newState[tx.tx_id];
        return newState;
      });
    }
  };

  // Deny pending request
  const handleDeny = async (tx) => {
    if (!window.confirm(`Are you sure you want to deny this request from ${tx.receiver_name}? This will delete the coin and cannot be undone.`)) {
      return;
    }

    setProcessingApproval(prev => ({ ...prev, [tx.tx_id]: 'denying' }));

    try {
      const idToken = auth.user.id_token;

      // Deny the transaction (deletes both transaction and coin)
      await apiService.denyTransaction(idToken, tx.tx_id);

      // Refresh pending approvals
      const sentData = await apiService.getTransactions(idToken, { type: 'sent', limit: 100 });
      const sent = sentData.transactions || [];
      const pendingRequests = sent.filter(tx => tx.pending === true || tx.pending === 1);
      setPendingApprovals(pendingRequests);

      // Clear editing state
      setEditingMessage(prev => {
        const newState = { ...prev };
        delete newState[tx.tx_id];
        return newState;
      });

      // Adjust current page if needed
      const newTotalPages = Math.ceil(pendingRequests.length / ITEMS_PER_PAGE);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    } catch (err) {
      console.error('Error denying request:', err);
      alert(`Failed to deny: ${err.message}`);
    } finally {
      setProcessingApproval(prev => {
        const newState = { ...prev };
        delete newState[tx.tx_id];
        return newState;
      });
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <main className="main-content">
        <LoadingSpinner message="Loading pending requests..." />
      </main>
    );
  }

  // Show error state
  if (error) {
    return (
      <main className="main-content">
        <ErrorDisplay
          title="Error Loading Pending Requests"
          message={error}
          onRetry={() => window.location.reload()}
        />
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="pending-requests-container">
        <BackButton to="/" />
        <div className="pending-requests-header">
          <h1>Pending Requests</h1>
        </div>

        {pendingApprovals.length === 0 ? (
          <EmptyState
            icon="✅"
            message="No pending coin requests at the moment"
          />
        ) : (
          <>
            <p className="pending-count">
              {pendingApprovals.length} pending request{pendingApprovals.length !== 1 ? 's' : ''}
            </p>

            <div className="approvals-list">
              {currentItems.map((tx) => {
                const currentMessage = editingMessage[tx.tx_id] !== undefined
                  ? editingMessage[tx.tx_id]
                  : tx.description;
                const isProcessing = processingApproval[tx.tx_id];

                return (
                  <div key={tx.tx_id} className="approval-item">
                    <div className="approval-header">
                      <div className="approval-badge">
                        <span className="badge-icon">
                          {tx.coin_type === 'individual' ? '🪙' : '🏆'}
                        </span>
                        <span className="badge-text">
                          {tx.coin_type === 'individual' ? 'Individual' : 'Organizational'}
                        </span>
                      </div>
                      <span className="approval-timestamp">{formatRelativeTime(tx.created_at)}</span>
                    </div>

                    <div className="approval-requester">
                      <strong>Requested by:</strong> {tx.receiver_name}
                    </div>

                    <div className="approval-message-edit">
                      <label htmlFor={`message-${tx.tx_id}`}>
                        Message (you can edit this before approving):
                      </label>
                      <RichTextEditor
                        value={currentMessage}
                        onChange={(newMessage) => handleMessageEdit(tx.tx_id, newMessage)}
                        disabled={isProcessing}
                        maxLength={10000}
                        placeholder="Edit the message..."
                      />
                    </div>

                    <div className="approval-actions">
                      <button
                        className="approve-button"
                        onClick={() => handleApprove(tx)}
                        disabled={isProcessing}
                      >
                        {isProcessing === 'approving' ? '✓ Approving...' : '✓ Approve'}
                      </button>
                      <button
                        className="deny-button"
                        onClick={() => handleDeny(tx)}
                        disabled={isProcessing}
                      >
                        {isProcessing === 'denying' ? '✗ Denying...' : '✗ Deny'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-button"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="pagination-button"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

export default PendingRequests;
